import { http } from '../http/client';
import type { ActionName, Dependency } from './permissionMatrix';

export interface MatrixResource {
  isSuperAdminOnly?: boolean;
  resourceId: string; code: string; label: string; title: string; url: string | null; icon: string | null;
  scopeBase: string | null; supported: ActionName[]; granted: ActionName[]; grantMax: ActionName[]; children: MatrixResource[];
  /** Extra scope areas this page reads on load (holding Read grants "<area>.read"). */
  readAlso?: string[] | null;
}
export interface PermissionMatrix {
  role: { id: string; code: string; name: string; description: string | null; isSystem: boolean; isFullAccess: boolean };
  productCode: string; version: number; openResourceCount: number; openScopesEnabled: boolean;
  tree: MatrixResource[]; dependencies: Dependency[]; supportedById: Record<string, number>;
}
export interface ManageableProduct { code: string; displayName: string; managedMenuCount: number }
export interface ResourceInput {
  productCode: string; parentId: string | null; code: string; label: string; title: string; url: string;
  icon: string; sortOrder: number; visibleInMenu: boolean; scopeBase: string; supported: ActionName[];
  description: string; grantToRoleIds: string[];
  /** Scope areas the page reads on load, e.g. ["restaurant.dining", "restaurant.catalog"]. */
  readAlso: string[];
}
export const permissionApi = {
  async products() { return (await http().get<ManageableProduct[]>('/authorization/manageable-products')).data; },
  async matrix(roleId: string, productCode: string) {
    return (await http().get<PermissionMatrix>(`/authorization/roles/${roleId}/matrix`, { params: { productCode } })).data;
  },
  async save(roleId: string, version: number, body: { productCode: string; grants: { resourceId: string; actions: ActionName[] }[] }) {
    return (await http().put<{ version: number }>(`/authorization/roles/${roleId}/matrix`, body, { headers: { 'If-Match': `"${version}"` } })).data;
  },
  async createResource(input: ResourceInput) { return (await http().post<{ id: string }>('/authorization/resources', input)).data; },
  async resource(id: string) {
    return (await http().get<{ id: string; productCode: string; code: string; label: string; title: string; url: string | null;
      icon: string | null; sortOrder: number | null; parentId: string | null; isHidden: boolean; scopeBase: string; supportedActions: number; description: string | null; readAlsoScopes: string | null }>(`/authorization/resources/${id}`)).data;
  },
  async updateResource(id: string, input: ResourceInput) { await http().put(`/authorization/resources/${id}`, input); },
  async deleteResource(id: string) { await http().delete(`/authorization/resources/${id}`); },
  async effective(membershipId: string, productCode: string) {
    return (await http().get<{ tree: MatrixResource[]; sources: { resourceId: string; via: Record<ActionName, string[]> }[] }>(
      `/authorization/memberships/${membershipId}/effective-access`, { params: { productCode } })).data;
  },
};
